import React, { useState } from 'react';
import { 
  UserX, 
  Trash2, 
  ShieldAlert, 
  Search, 
  History, 
  Calendar, 
  Wallet, 
  AlertTriangle,
  Lock,
  Eye,
  CheckCircle2,
  FileSpreadsheet
} from 'lucide-react';
import { StatusBadge } from '../components/common/StatusBadge';
import { Modal } from '../components/common/Modal';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { DeletedAccountRecord } from '../types/admin';

export const DeletedAccountsView: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRecord, setSelectedRecord] = useState<DeletedAccountRecord | null>(null);
  const [recordToPurge, setRecordToPurge] = useState<DeletedAccountRecord | null>(null);
  const [purgeLoading, setPurgeLoading] = useState(false);

  // Sample data representing accounts deleted by users via in-app settings
  const [deletedAccounts, setDeletedAccounts] = useState<DeletedAccountRecord[]>([
    {
      id: 'del_acc_901',
      originalUid: 'usr_del_ade_092',
      name: 'Adewale Balogun',
      email: 'adewale.b@gmail.com',
      phone: '+234 803 771 9920',
      registeredAt: '2026-03-12',
      deletedAt: '2026-09-15 14:22:01',
      deactivationReason: 'Relocating to the United Kingdom, no longer need Nigerian local trading account.',
      lastWalletBalance: 0,
      tradesCompleted: 7,
      transactionsHistory: [
        { id: 'tx_9812', type: 'escrow_settlement', amount: 32000, description: 'Sold Sony WH-1000XM4 Headphones', createdAt: '2026-08-20', status: 'settled' },
        { id: 'tx_9410', type: 'wallet_withdrawal', amount: 32000, description: 'Transferred funds to GTBank account', createdAt: '2026-08-21', status: 'successful' },
        { id: 'tx_8911', type: 'escrow_deposit', amount: 15000, description: 'Bought Retro Desk Lamp', createdAt: '2026-06-14', status: 'settled' },
      ]
    },
    {
      id: 'del_acc_902',
      originalUid: 'usr_del_sarah_114',
      name: 'Sarah Nwachukwu',
      email: 'sarah.nw@yahoo.com',
      phone: '+234 812 449 0192',
      registeredAt: '2026-01-05',
      deletedAt: '2026-09-12 11:08:45',
      deactivationReason: 'Found another alternative platform / too many notifications.',
      lastWalletBalance: 500,
      tradesCompleted: 3,
      transactionsHistory: [
        { id: 'tx_7812', type: 'wallet_funding', amount: 20000, description: 'Funded wallet via Flutterwave', createdAt: '2026-05-10', status: 'successful' },
        { id: 'tx_7813', type: 'escrow_deposit', amount: 19500, description: 'Bought Vintage Denim Jacket', createdAt: '2026-05-10', status: 'settled' },
      ]
    },
    {
      id: 'del_acc_903',
      originalUid: 'usr_del_uche_842',
      name: 'Uchechukwu Sunday',
      email: 'uche.sun@outlook.com',
      phone: '+234 809 900 1284',
      registeredAt: '2026-07-22',
      deletedAt: '2026-09-08 19:40:12',
      deactivationReason: 'Temporary digital detox; will recreate account next year.',
      lastWalletBalance: 0,
      tradesCompleted: 1,
      transactionsHistory: [
        { id: 'tx_6619', type: 'escrow_settlement', amount: 8500, description: 'Sold Harry Potter Book Set', createdAt: '2026-08-01', status: 'settled' },
        { id: 'tx_6620', type: 'wallet_withdrawal', amount: 8500, description: 'Bank Payout to Zenith Bank', createdAt: '2026-08-02', status: 'successful' },
      ]
    }
  ]);

  const handlePurgeRecord = async () => {
    if (!recordToPurge) return;
    setPurgeLoading(true);
    try {
      // Simulate scrub action
      await new Promise(r => setTimeout(r, 600));
      setDeletedAccounts(prev => prev.filter(a => a.id !== recordToPurge.id));
      setRecordToPurge(null);
      if (selectedRecord?.id === recordToPurge.id) {
        setSelectedRecord(null);
      }
    } finally {
      setPurgeLoading(false);
    }
  };

  const filtered = deletedAccounts.filter(acc => 
    acc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    acc.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    acc.originalUid.toLowerCase().includes(searchQuery.toLowerCase()) ||
    acc.phone?.includes(searchQuery)
  );

  return (
    <div className="space-y-6">
      {/* Regulatory & Security Compliance Banner */}
      <div className="p-5 rounded-3xl border transition-colors flex flex-col md:flex-row items-start md:items-center justify-between gap-4
        bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
        light:bg-white light:border-[#D5E4DC] light:shadow-sm
      ">
        <div className="flex items-start gap-3.5">
          <div className="p-3 rounded-2xl bg-rose-500/15 border border-rose-500/30 text-rose-400 shrink-0">
            <Lock className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17]">
                Deleted Accounts Security & Audit Log
              </h3>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-500/20 text-rose-400 border border-rose-500/30 uppercase">
                Restricted Access
              </span>
            </div>
            <p className="mt-1 text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] max-w-3xl leading-relaxed">
              In accordance with financial compliance & escrow fraud prevention, accounts deleted by mobile users are quarantined here. Identity records and transaction histories are preserved for Super Admin security audits, with an explicit <strong>Hard Delete (Scrub / Purge)</strong> option available for Right-to-be-Forgotten compliance.
            </p>
          </div>
        </div>

        <div className="px-4 py-2 rounded-2xl bg-[#10231d] dark:bg-[#10231d] border border-[#22493c] dark:border-[#22493c] light:bg-gray-50 light:border-gray-200 shrink-0">
          <span className="text-[11px] text-[#90cbb7] light:text-gray-500">Quarantined Records</span>
          <p className="text-lg font-bold text-white dark:text-white light:text-[#0D1C17]">{deletedAccounts.length}</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="flex items-center justify-between gap-4 p-4 rounded-2xl border
        bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
        light:bg-white light:border-[#D5E4DC]
      ">
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search deleted records by name, email, or UID..."
            className="w-full pl-9 pr-4 py-2 rounded-xl text-xs outline-none border transition-colors
              bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
              light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
              focus:border-brand-500"
          />
        </div>

        <span className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
          Showing {filtered.length} of {deletedAccounts.length} archived accounts
        </span>
      </div>

      {/* Deleted Accounts Table */}
      <div className="rounded-3xl border overflow-hidden transition-colors
        bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
        light:bg-white light:border-[#D5E4DC] light:shadow-sm
      ">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] uppercase tracking-wider font-semibold">
                <th className="py-4 px-6">Deactivated User</th>
                <th className="py-4 px-6">UID & Contact</th>
                <th className="py-4 px-6">Deactivation Date</th>
                <th className="py-4 px-6">Deletion Reason</th>
                <th className="py-4 px-6">Past Trades</th>
                <th className="py-4 px-6 text-right">Security Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#22493c] dark:divide-[#22493c] light:divide-[#EBF2EE]">
              {filtered.map((acc) => (
                <tr key={acc.id} className="hover:bg-[#1a382e]/50 dark:hover:bg-[#1a382e]/50 light:hover:bg-gray-50/80 transition-colors">
                  <td className="py-4 px-6">
                    <div className="font-bold text-sm text-white dark:text-white light:text-[#0D1C17]">
                      {acc.name}
                    </div>
                    <div className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      Registered: {acc.registeredAt}
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="font-mono text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      {acc.originalUid}
                    </div>
                    <div className="text-white dark:text-white light:text-[#0D1C17] font-medium">
                      {acc.email}
                    </div>
                    <div className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      {acc.phone || 'No phone logged'}
                    </div>
                  </td>
                  <td className="py-4 px-6 font-medium text-white dark:text-white light:text-[#0D1C17]">
                    {acc.deletedAt}
                  </td>
                  <td className="py-4 px-6 max-w-xs">
                    <p className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] truncate" title={acc.deactivationReason}>
                      "{acc.deactivationReason}"
                    </p>
                  </td>
                  <td className="py-4 px-6">
                    <span className="font-bold text-brand-400">{acc.tradesCompleted} orders</span>
                    <div className="text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      Bal at exit: ₦{acc.lastWalletBalance}
                    </div>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setSelectedRecord(acc)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#22493c] text-[#90cbb7] hover:text-white hover:bg-[#2a5547] light:bg-gray-100 light:text-gray-700 light:hover:bg-gray-200 transition-colors"
                      >
                        <History className="w-3.5 h-3.5" />
                        <span>Inspect Ledger</span>
                      </button>

                      <button
                        onClick={() => setRecordToPurge(acc)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 border border-rose-500/20 transition-colors"
                        title="Completely scrub this user's records from system"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Hard Delete (Scrub)</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Historical Transaction Ledger Inspection Modal */}
      {selectedRecord && (
        <Modal
          isOpen={Boolean(selectedRecord)}
          onClose={() => setSelectedRecord(null)}
          title={`Audit Dossier: ${selectedRecord.name} (Deactivated)`}
          subtitle={`Original UID: ${selectedRecord.originalUid} • Deactivated: ${selectedRecord.deletedAt}`}
          maxWidth="2xl"
        >
          <div className="space-y-6 text-xs">
            <div className="p-4 rounded-2xl bg-[#10231d] dark:bg-[#10231d] light:bg-gray-50 border border-[#22493c] dark:border-[#22493c] light:border-gray-200">
              <h5 className="font-bold text-white dark:text-white light:text-gray-900 mb-1">
                Deactivation Reason Given:
              </h5>
              <p className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] italic">
                "{selectedRecord.deactivationReason}"
              </p>
            </div>

            <div>
              <h5 className="font-bold text-white dark:text-white light:text-gray-900 mb-3 flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-brand-400" />
                <span>Historical Transaction Ledger Records</span>
              </h5>

              {selectedRecord.transactionsHistory && selectedRecord.transactionsHistory.length > 0 ? (
                <div className="rounded-2xl border border-[#22493c] dark:border-[#22493c] light:border-gray-200 overflow-hidden">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-[#10231d] dark:bg-[#10231d] light:bg-gray-100 border-b border-[#22493c] dark:border-[#22493c] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] uppercase font-semibold text-[10px]">
                        <th className="py-2.5 px-4">Ref ID</th>
                        <th className="py-2.5 px-4">Description</th>
                        <th className="py-2.5 px-4">Amount</th>
                        <th className="py-2.5 px-4">Date</th>
                        <th className="py-2.5 px-4">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#22493c] dark:divide-[#22493c] light:divide-gray-200">
                      {selectedRecord.transactionsHistory.map(tx => (
                        <tr key={tx.id}>
                          <td className="py-2.5 px-4 font-mono text-[11px] text-[#90cbb7] light:text-gray-600">{tx.id}</td>
                          <td className="py-2.5 px-4 font-medium text-white dark:text-white light:text-gray-900">{tx.description}</td>
                          <td className="py-2.5 px-4 font-bold text-white dark:text-white light:text-gray-900">₦{tx.amount.toLocaleString()}</td>
                          <td className="py-2.5 px-4 text-[#90cbb7] light:text-gray-500">{tx.createdAt}</td>
                          <td className="py-2.5 px-4">
                            <StatusBadge status={tx.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-[#90cbb7] light:text-gray-500 italic">No previous ledger records stored for this account.</p>
              )}
            </div>

            <div className="pt-4 border-t border-[#22493c] dark:border-[#22493c] flex justify-between items-center">
              <span className="text-[11px] text-[#90cbb7] light:text-gray-500">
                Retained securely in compliance with Central Bank of Nigeria / AML regulations.
              </span>
              <button
                onClick={() => setSelectedRecord(null)}
                className="px-4 py-2 rounded-full text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547]"
              >
                Close Dossier
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Two-Factor Confirmation Dialog for Hard Scrub / Purge */}
      {recordToPurge && (
        <ConfirmDialog
          isOpen={Boolean(recordToPurge)}
          onClose={() => setRecordToPurge(null)}
          onConfirm={handlePurgeRecord}
          title={`Hard Delete & Scrub: ${recordToPurge.name}?`}
          description={`WARNING: This action is permanent and completely unrecoverable. It will permanently scrub all retained identity records, phone numbers, email linkages, and transaction ledgers for UID ${recordToPurge.originalUid} from the database.`}
          confirmWord="PURGE"
          confirmButtonText="Permanently Scrub Account"
          variant="danger"
          loading={purgeLoading}
        />
      )}
    </div>
  );
};
